import {getGroupProduct, getSalaryDeductionOccupationClassInfo} from 'ApplicationEntry/api/awdApi';
import ApplicationEntryRouteConstants from 'ApplicationEntry/ApplicationEntryRouteConstants';
import * as D from 'ApplicationEntry/ducks/ProductsRiders/disability';
import {push} from 'connected-react-router';
import {AsyncResult, Loaded} from 'Core/models/Async';
import {all, call, put, takeLatest} from 'redux-saga/effects';
import {
  SalaryDeductionOccupationClassInfo,
  SalaryDeductionOccupationClassInfoWarning,
} from 'ApplicationEntry/models/ProductsRiders/SalaryDeductionOccupationClassModel';
import * as E from 'fp-ts/lib/Either';

function* getSalaryDeductionOccupationClass() {
  yield takeLatest(D.DisabilityActionType.GET_SALD_OCC_CLASS, function*(action: ReturnType<typeof D.getSaldOccClass>) {
    const {payload} = action;
    const {caseNumber, plan} = payload;
    const {life70Name} = plan;

    const response: AsyncResult<SalaryDeductionOccupationClassInfo> = yield call(
      getSalaryDeductionOccupationClassInfo,
      caseNumber,
      life70Name!
    );

    if (E.isRight(response)) {
      yield put(D.changeSaldOccClass(new Loaded(response.right)));
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      if (
        response.left.key === 'NotFound' &&
        response.left.errorDetails!.messages.some(
          (m: {message: string; responseSeverity: string}) =>
            m.message === 'Description: Salary deduction information for the provided input was not found.'
        )
      ) {
        yield put(
          D.changeSaldOccClass(
            new SalaryDeductionOccupationClassInfoWarning(
              'An occupation class that is based on the provided arguments could not be found.'
            )
          )
        );
      } else {
        yield put(D.changeSaldOccClass(response.left));
      }
    }
  });
}

function* getPlanType() {
  yield takeLatest(D.DisabilityActionType.GET_PLAN_TYPE, function*(action: ReturnType<typeof D.getPlanType>) {
    const planTypes = ['DI5W'];
    yield put(D.changePlanTypeApi(new Loaded(planTypes)));
  });
}

function* getType() {
  yield takeLatest(D.DisabilityActionType.GET_TYPE, function*(action: ReturnType<typeof D.getType>) {
    const types = ['Simplified', 'Select'];
    yield put(D.changeTypeApi(new Loaded(types)));
  });
}

function* getAccident() {
  yield takeLatest(D.DisabilityActionType.GET_ACCIDENT, function*(action: ReturnType<typeof D.getAccident>) {
    const accident = ['000', '007', '014', '030', '090', '180'];
    yield put(D.changeAccidentApi(new Loaded(accident)));
  });
}

function* getSickness() {
  yield takeLatest(D.DisabilityActionType.GET_SICKNESS, function*(action: ReturnType<typeof D.getSickness>) {
    const sickness = ['007', '014', '030', '090', '180'];
    yield put(D.changeSicknessApi(new Loaded(sickness)));
  });
}

function* getBenefitPeriod() {
  yield takeLatest(D.DisabilityActionType.GET_BENEFIT_PERIOD, function*(action: ReturnType<typeof D.getBenefitPeriod>) {
    let benefitPeriod;
    if (action.payload.type === 'Simplified') {
      benefitPeriod = [3, 6, 12, 24, 60];
    } else {
      benefitPeriod = [3, 6, 12, 24];
    }
    yield put(D.changeBenefitPeriodApi(new Loaded(benefitPeriod)));
  });
}

function* getAccidentRiderUnits() {
  yield takeLatest(D.DisabilityActionType.GET_ACCIDENT_RIDER_UNIT, function*(
    action: ReturnType<typeof D.getAccidentRiderUnits>
  ) {
    const riderUnits = [0, 0.5, 1];
    yield put(D.changeAccidentRiderUnitsApi(new Loaded(riderUnits)));
  });
}

function* getRiderType() {
  yield takeLatest(D.DisabilityActionType.GET_RIDER_TYPE, function*(action: ReturnType<typeof D.getRiderType>) {
    const riderTypes = ['Individual', 'Family'];
    yield put(D.changeRiderTypeApi(new Loaded(riderTypes)));
  });
}

function* getOccClass() {
  yield takeLatest(D.DisabilityActionType.GET_OCC_CLASS, function*(action: ReturnType<typeof D.getOccClass>) {
    const occClass = ['P', 'S'];
    yield put(D.changeOccClassApi(new Loaded(occClass)));
  });
}

function* getPackageSourceList() {
  yield takeLatest(D.DisabilityActionType.GET_PACKAGE_SOURCE_LIST, function*(
    action: ReturnType<typeof D.getPackageSourceList>
  ) {
    const {payload} = action;
    const {caseNumber, planCode} = payload;

    const response: AsyncResult<{packages: object[]}> = yield call(getGroupProduct, caseNumber, planCode);

    if (E.isRight(response)) {
      yield put(D.changePackageSourceApi(new Loaded(response.right.packages)));
    } else if (response.left.key === 'Unauthorized') {
      yield put(push(ApplicationEntryRouteConstants.AWDLogin));
    } else {
      yield put(D.changePackageSourceApi(response.left));
    }
  });
}

export default function*() {
  yield all([
    getPlanType(),
    getType(),
    getAccident(),
    getSickness(),
    getBenefitPeriod(),
    getAccidentRiderUnits(),
    getRiderType(),
    getOccClass(),
    getPackageSourceList(),
    getSalaryDeductionOccupationClass(),
  ]);
}
